/**
 * NextBlockedItems Component
 *
 * Shows units and jobs that need attention due to compliance issues.
 * Provides quick navigation to blocked items to unblock project progress.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowRight, FileX, CheckCircle } from 'lucide-react';
import type { Unit } from '@/types/project';

interface BlockedJob {
  id: string;
  title: string;
  status: string;
  permitType?: string;
  complianceStatus?: string;
  unitId?: string;
}

interface NextBlockedItemsProps {
  units: Unit[];
  jobs: BlockedJob[];
  projectId: string;
}

export function NextBlockedItems({ units, jobs, projectId }: NextBlockedItemsProps) {
  const navigate = useNavigate();

  // Find blocked units (units with blocked jobs)
  const blockedUnits = useMemo(() => {
    return units.filter((unit) => unit.blockedJobs > 0);
  }, [units]);

  // Find blocked jobs (jobs with failing compliance)
  const blockedJobs = useMemo(() => {
    return jobs.filter((job) => job.complianceStatus === 'fail');
  }, [jobs]);

  // If nothing is blocked, show success state
  if (blockedUnits.length === 0 && blockedJobs.length === 0) {
    return (
      <Card className="border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-950/20">
        <CardContent className="py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-full">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                No Blocked Items
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                All units and jobs are on track
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-yellow-200 dark:border-yellow-900">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          <CardTitle className="text-base">Needs Attention</CardTitle>
        </div>
        <CardDescription>
          {blockedUnits.length + blockedJobs.length} item{blockedUnits.length + blockedJobs.length !== 1 ? 's' : ''} require action to continue
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Blocked Jobs (show first 5) */}
        {blockedJobs.slice(0, 5).map((job) => {
          const unit = units.find((u) => u.id === job.unitId);
          return (
            <div
              key={job.id}
              className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-950/50 transition-colors"
              onClick={() => navigate(`/jobs/${job.id}`)}
            >
              <div className="flex items-center gap-3">
                <FileX className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <p className="font-medium text-sm">{job.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {unit ? `${unit.label} • ` : ''}{job.permitType || 'General'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="destructive" className="text-xs">
                  Blocked
                </Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          );
        })}

        {/* Show more link if there are more blocked items */}
        {blockedJobs.length > 5 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => {/* Could navigate to filtered jobs view */}}
          >
            +{blockedJobs.length - 5} more blocked jobs
          </Button>
        )}

        {/* Summary of blocked units */}
        {blockedUnits.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              <strong>{blockedUnits.length}</strong> unit{blockedUnits.length !== 1 ? 's' : ''} with blocked jobs:{' '}
              {blockedUnits.slice(0, 5).map((u) => u.label).join(', ')}
              {blockedUnits.length > 5 && `, +${blockedUnits.length - 5} more`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
